import {useEffect, useState} from 'react';
import Cookies from 'js-cookie';
import {
  GEOBLOCKING_STATUS,
  GEOBLOCKING_STATUS_EVENT,
  GEOBLOCKING_SELECTED_ZONE_COOKIE,
} from '../geoblocking-constants';

/**
 * React hook to retrieve the currently selected geoblocking zone.
 *
 * @returns {string|null} The selected zone identifier from cookie, or null if not set.
 * Possible values: a region code (e.g., "US", "CA"), or null if no zone is selected.
 *
 * Note: The external geoblocking script sets the 'selectedZone' cookie on page load
 * and dispatches a 'geoblocking:status' event when initialization is complete.
 */
export function useSelectedZone() {
  const [selectedZone, setSelectedZone] = useState(() => Cookies.get(GEOBLOCKING_SELECTED_ZONE_COOKIE) || null);

  useEffect(() => {
    // If we already have a zone, no need to wait for events
    if (selectedZone) return;

    // If geoblocking is not loading, check cookie immediately
    if (globalThis.storeVariables?.geoblockingStatus !== GEOBLOCKING_STATUS.LOADING) {
      setSelectedZone(Cookies.get(GEOBLOCKING_SELECTED_ZONE_COOKIE) || null);
      return;
    }

    // Otherwise, wait for geoblocking to finish initializing
    const handleGeoblockingStatus = (event) => {
      const status = event.detail?.status;
      if (status !== GEOBLOCKING_STATUS.ENABLED && status !== GEOBLOCKING_STATUS.DISABLED) return;

      setSelectedZone(Cookies.get(GEOBLOCKING_SELECTED_ZONE_COOKIE) || null);
    };

    document.addEventListener(GEOBLOCKING_STATUS_EVENT, handleGeoblockingStatus);

    return () => {
      document.removeEventListener(GEOBLOCKING_STATUS_EVENT, handleGeoblockingStatus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once at mount to set up geoblocking listener

  return selectedZone;
}
